import { formatCurrency } from '@if/core/utils'
import EventNoteIcon from '@mui/icons-material/EventNote'
import HelpIcon from '@mui/icons-material/Help'
import { Button, Tooltip, Typography } from '@mui/material'
import { useRefreshSimulation } from '@src/api/loan/loan-api'
import { useDeleteMetro2Reporting, usePostMetro2Reporting } from '@src/api/loan/loan-metro2reporting'
import { transformMetro2ReportingToDto } from '@src/api/loan/loan-transformer'
import { reportErrorToConsole } from '@src/services/error-logger'
import { Metro2Reporting } from '@src/types/loan/loan-calendar'
import { SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import InvoiceRow from '../../Common/Table/InvoiceRow'
import InvoiceTable from '../../Common/Table/InvoiceTable'
import {
  useLoanCalendarActivationOn,
  useLoanCalendarMetro2Reportings,
  useLoanSummaryInformation,
} from '../LoanCalendar-hooks'
import { useLoanCalendarSimulation } from '../LoanCalendarSimulationProvider'
import Metro2ReportingDialog from '../Metro2Reporting/Metro2ReportingDialog'

interface Props {
  signedSimulation: SignedCalendarSimulation
}

const getCurrentMetro2Reporting = (metro2Reportings: Metro2Reporting[] | null): Metro2Reporting | null => {
  if (!metro2Reportings || metro2Reportings?.length === 0) return null
  const latestMetro2Reporting: Metro2Reporting | null = metro2Reportings.reduce((prev, current) => {
    return prev.createdOn > current.createdOn ? prev : current
  })
  return latestMetro2Reporting || null
}

const LoanCreditReportCardContent = ({ signedSimulation }: Props) => {
  const { t } = useTranslation()
  const { addSimulationAction } = useLoanCalendarSimulation()
  const simulation = signedSimulation.simulation
  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const { mutateAsync: postMetro2Reporting } = usePostMetro2Reporting(
    simulation.fullLoan.loan.id,
    simulation.fullLoan.loan.financingProgramId,
  )
  const { mutateAsync: deleteMetro2Reporting } = useDeleteMetro2Reporting(
    simulation.fullLoan.loan.id,
    simulation.fullLoan.loan.financingProgramId,
  )
  const { mutateAsync: refreshSimulation } = useRefreshSimulation(
    simulation.fullLoan.loan.id,
    simulation.fullLoan.loan.financingProgramId,
  )

  const summaryInfo = useLoanSummaryInformation(simulation)
  const metro2Reportings = useLoanCalendarMetro2Reportings(simulation)
  const metro2CurrentReporting = getCurrentMetro2Reporting(metro2Reportings)
  const activationOn = useLoanCalendarActivationOn(simulation)

  const handleOnMetro2ReportingSave = (metro2Reporting: Metro2Reporting | null) => {
    if (metro2CurrentReporting && !metro2Reporting) {
      // Delete the currentMetro2Reporting
      deleteMetro2Reporting()
        .then(async () => {
          setOpenDialog(false)
          // Refresh the simulation, so the update of the Metro2Reporting does not impact the actions made by the user
          const refreshedSimulation = await refreshSimulation(signedSimulation).catch(reportErrorToConsole)
          if (refreshedSimulation) addSimulationAction(refreshedSimulation)
        })
        .catch(reportErrorToConsole)
    } else if (metro2Reporting !== metro2CurrentReporting) {
      // Update the currentMetro2Reporting
      postMetro2Reporting(transformMetro2ReportingToDto(metro2Reporting!))
        .then(async () => {
          setOpenDialog(false)
          // Refresh the simulation, so the update of the Metro2Reporting does not impact the actions made by the user
          const refreshedSimulation = await refreshSimulation(signedSimulation).catch(reportErrorToConsole)
          if (refreshedSimulation) addSimulationAction(refreshedSimulation)
        })
        .catch(reportErrorToConsole)
    }
  }

  return (
    <>
      <InvoiceTable>
        <InvoiceRow
          label={t('loan.payment.creditReportCard.lateStartDate')}
          value={
            metro2CurrentReporting ? (
              <Typography variant="inherit" fontWeight="bold">
                {metro2CurrentReporting.metro2OverrideDate.toString()}
              </Typography>
            ) : (
              activationOn.toString()
            )
          }
          actions={
            <Tooltip title={t('loan.payment.creditReportCard.changeLateStartDate')}>
              <Button onClick={() => setOpenDialog(true)} style={{ minWidth: 'auto', padding: 0 }}>
                <EventNoteIcon color="primary" fontSize="inherit" />
              </Button>
            </Tooltip>
          }
        />
        <InvoiceRow
          label={t('loan.payment.creditReportCard.firstDelinquencyDate')}
          value={
            summaryInfo.metro2DateFirstDelinquency
              ? summaryInfo.metro2DateFirstDelinquency.toString()
              : t('common.none')
          }
          actions={
            <Tooltip title={t('loan.payment.creditReportCard.dateFirstDelinquencyDetails')}>
              <HelpIcon color="primary" fontSize="inherit" />
            </Tooltip>
          }
        />
        <InvoiceRow
          label={t('loan.payment.creditReportCard.accountStatus')}
          value={
            <Typography variant="inherit" fontWeight="bold">
              {t(
                `loan.payment.creditReportCard.statusByMissingPayments.accountStatus${summaryInfo.metro2AccountStatus}`,
              )}
            </Typography>
          }
        />
        <InvoiceRow
          label={t('loan.payment.creditReportCard.lateAmount')}
          value={formatCurrency(summaryInfo.metro2LateAmount ?? 0)}
        />
        <InvoiceRow
          label={t('loan.payment.creditReportCard.reportingDate')}
          value={summaryInfo.metro2DateReporting ? summaryInfo.metro2DateReporting.toString() : t('common.none')}
        />
      </InvoiceTable>
      <Metro2ReportingDialog
        open={openDialog}
        onCancel={() => {
          setOpenDialog(false)
        }}
        onSave={(data: Metro2Reporting | null) => {
          handleOnMetro2ReportingSave(data)
        }}
        currentMetro2Reporting={metro2CurrentReporting}
        historyMetro2Reportings={metro2Reportings}
      />
    </>
  )
}

export default LoanCreditReportCardContent
